/**
 * Smart model routing for automatic tier-based model selection.
 *
 * Analyzes conversation signals after each turn and selects the optimal model
 * tier (fast/balanced/powerful) for the next turn based on task complexity.
 */

import type { AgentMessage, ThinkingLevel } from "@elyracode/agent-core";
import type { Api, AssistantMessage, Model, ToolCall, ToolResultMessage, UserMessage } from "@elyracode/ai";
import { modelsAreEqual } from "@elyracode/ai";

// ============================================================================
// Types
// ============================================================================

export interface SmartRouterConfig {
	enabled: boolean;
	/** Available models the router can pick from. Must have at least one. */
	availableModels: Model<Api>[];
	/** The user's preferred/current model -- used as the Tier 2 default and fallback. */
	preferredModel: Model<Api>;
	/**
	 * User-pinned models per tier (from settings). When set, a pin wins over
	 * the heuristic ranking for its tier — the router decides the tier, the
	 * user decides the model. Pins are still subject to the modality filter.
	 */
	pinnedModels?: Partial<Record<ComplexityTier, Model<Api>>>;
	/**
	 * During a budgeted /goal loop, swap the routed model for its half-price
	 * `:batch` sibling on the same provider when one exists. Unattended goal
	 * work tolerates batch latency; interactive turns are never swapped
	 * (the swap only applies while a goal budget is active).
	 */
	preferBatchDuringGoal?: boolean;
}

export interface TurnAnalysis {
	/** The messages in the conversation so far. */
	messages: readonly AgentMessage[];
	/** Current thinking level. */
	thinkingLevel: ThinkingLevel;
	/**
	 * Fraction (0..1+) of an active goal budget already spent, when a
	 * budgeted /goal is running. Used to cap the tier as funds run low.
	 */
	budgetUsedFraction?: number;
}

export type ComplexityTier = "fast" | "balanced" | "powerful";

export interface SmartRouterResult {
	model: Model<Api>;
	tier: ComplexityTier;
	reason: string;
	/** False when the selected model is the one already in use. */
	changed: boolean;
	/**
	 * Thinking level for this turn, when routing lowers it below the session
	 * setting. Undefined means keep the session level. Routing only ever
	 * reduces effort - the user's chosen level is a ceiling, never a floor.
	 */
	thinkingLevel?: ThinkingLevel;
}

// ============================================================================
// Constants
// ============================================================================

/** Output cost thresholds ($/million tokens) for tier classification. */
const TIER_1_MAX_COST = 3;
const TIER_3_MIN_COST = 20;

/** Tool names considered read-only (no side effects). */
const READ_ONLY_TOOLS: ReadonlySet<string> = new Set(["read", "grep", "find", "ls"]);

/** Keywords in user messages that signal complex work. */
const COMPLEX_KEYWORDS: readonly string[] = [
	"refactor",
	"architect",
	"redesign",
	"migrate",
	"rewrite",
	"debug",
	"fix the bug",
	"security audit",
	"optimize",
	"performance",
	"investigate",
	"diagnose",
	"troubleshoot",
	"root cause",
	"race condition",
	"deadlock",
	"memory leak",
	"regression",
	"breaking change",
	"backwards compatible",
	"design pattern",
	"abstraction",
	"inheritance",
	"polymorphism",
	"dependency injection",
	"restructure",
	"consolidate",
	"decompose",
	"decouple",
	"integrate",
	"implement from scratch",
	"build a system",
	"complex",
	"tricky",
	"subtle",
];

/** Short user message threshold (characters). */
const SHORT_MESSAGE_THRESHOLD = 100;

/**
 * User-turn count threshold for complex multi-step tasks.
 * Counts only user messages: raw message counts inflate immediately because
 * every tool result is its own message, which made earlier versions escalate
 * to powerful within two or three turns.
 */
const LONG_CONVERSATION_THRESHOLD = 20;

/** File edit count threshold per turn for complexity escalation. */
const MANY_EDITS_THRESHOLD = 3;

/** Character count threshold for large tool output. */
const LARGE_TOOL_OUTPUT_THRESHOLD = 5000;

/** Conversation length below which read-only de-escalation to fast is allowed. */
const READ_ONLY_DEESCALATION_MAX_MESSAGES = 10;

/** Above this fraction of a goal budget, cap routing at the balanced tier. */
const BUDGET_CAP_BALANCED_FRACTION = 0.75;

/** Above this fraction of a goal budget, cap routing at the fast tier. */
const BUDGET_CAP_FAST_FRACTION = 0.9;

// ============================================================================
// Helpers
// ============================================================================

function isUserMessage(msg: AgentMessage): msg is UserMessage {
	return "role" in msg && (msg as UserMessage).role === "user";
}

function isAssistantMessage(msg: AgentMessage): msg is AssistantMessage {
	return "role" in msg && (msg as AssistantMessage).role === "assistant";
}

function isToolResultMessage(msg: AgentMessage): msg is ToolResultMessage {
	return "role" in msg && (msg as ToolResultMessage).role === "toolResult";
}

/** Extract the plain-text content of a user message. */
function getUserMessageText(msg: UserMessage): string {
	if (typeof msg.content === "string") return msg.content;
	return msg.content
		.filter((c): c is { type: "text"; text: string } => c.type === "text")
		.map((c) => c.text)
		.join(" ");
}

/** Extract tool calls from an assistant message's content. */
function getToolCalls(msg: AssistantMessage): ToolCall[] {
	return msg.content.filter((c): c is ToolCall => c.type === "toolCall");
}

/** True when all tool calls in a message are read-only. */
function allToolCallsReadOnly(toolCalls: ToolCall[]): boolean {
	return toolCalls.length > 0 && toolCalls.every((tc) => READ_ONLY_TOOLS.has(tc.name));
}

/** Count write/edit tool calls in an assistant message. */
function countFileEditCalls(toolCalls: ToolCall[]): number {
	return toolCalls.filter((tc) => tc.name === "write" || tc.name === "edit").length;
}

/**
 * Collect tool result messages that follow an assistant message.
 * Walks forward from the assistant message index, collecting consecutive
 * tool results (there may be other message types interleaved in custom
 * AgentMessage streams, so we stop at the next user or assistant message).
 */
function getToolResultsAfter(messages: readonly AgentMessage[], assistantIndex: number): ToolResultMessage[] {
	const results: ToolResultMessage[] = [];
	for (let i = assistantIndex + 1; i < messages.length; i++) {
		const msg = messages[i];
		if (isUserMessage(msg) || isAssistantMessage(msg)) break;
		if (isToolResultMessage(msg)) results.push(msg);
	}
	return results;
}

/** True when the text contains any of the complex keywords (case-insensitive). */
function containsComplexKeyword(text: string): boolean {
	const lower = text.toLowerCase();
	return COMPLEX_KEYWORDS.some((kw) => lower.includes(kw));
}

/** True when any user message in the conversation contains an image. */
export function conversationHasImages(messages: readonly AgentMessage[]): boolean {
	for (const msg of messages) {
		if (!isUserMessage(msg)) continue;
		if (typeof msg.content === "string") continue;
		if (msg.content.some((c) => c.type === "image")) return true;
	}
	return false;
}

// ============================================================================
// Tier classification
// ============================================================================

/** Bucket an output cost ($/million tokens) into a complexity tier. */
export function tierForOutputCost(outputCostPerMillion: number): ComplexityTier {
	if (outputCostPerMillion < TIER_1_MAX_COST) return "fast";
	if (outputCostPerMillion < TIER_3_MIN_COST) return "balanced";
	return "powerful";
}

/** Classify a model into its cost tier. */
function modelTier(model: Model<Api>): ComplexityTier {
	return tierForOutputCost(model.cost.output);
}

/** Measure the total text length of a tool result message. */
function toolResultTextLength(tr: ToolResultMessage): number {
	return tr.content
		.filter((c): c is { type: "text"; text: string } => c.type === "text")
		.reduce((sum, c) => sum + c.text.length, 0);
}

/** Classify the complexity tier for the next turn. */
export function classifyComplexity(
	analysis: TurnAnalysis,
	previousTier?: ComplexityTier,
): { tier: ComplexityTier; reason: string } {
	const { messages, thinkingLevel } = analysis;

	// High thinking level -> complex
	if (thinkingLevel === "high" || thinkingLevel === "xhigh") {
		return { tier: "powerful", reason: `thinking level is set to ${thinkingLevel}` };
	}

	// Long conversation (counted in user turns, not raw messages) -> complex
	const userTurnCount = messages.filter(isUserMessage).length;
	if (userTurnCount > LONG_CONVERSATION_THRESHOLD) {
		return { tier: "powerful", reason: `long conversation (${userTurnCount} user turns)` };
	}

	// Find the last assistant message and its tool results
	let lastAssistantIndex = -1;
	for (let i = messages.length - 1; i >= 0; i--) {
		if (isAssistantMessage(messages[i])) {
			lastAssistantIndex = i;
			break;
		}
	}

	// Find the last user message
	let lastUserMessage: UserMessage | undefined;
	for (let i = messages.length - 1; i >= 0; i--) {
		if (isUserMessage(messages[i])) {
			lastUserMessage = messages[i] as UserMessage;
			break;
		}
	}

	// Check for complex signals from the last assistant turn
	if (lastAssistantIndex >= 0) {
		const lastAssistant = messages[lastAssistantIndex] as AssistantMessage;
		const toolCalls = getToolCalls(lastAssistant);
		const toolResults = getToolResultsAfter(messages, lastAssistantIndex);

		// Errors in last turn -> complex
		const hasErrors = toolResults.some((tr) => tr.isError);
		if (hasErrors) {
			return { tier: "powerful", reason: "tool errors in last turn" };
		}

		// Many file edits in last turn -> complex
		if (countFileEditCalls(toolCalls) > MANY_EDITS_THRESHOLD) {
			return applyHysteresis(
				{ tier: "powerful", reason: `many file edits in last turn (${countFileEditCalls(toolCalls)})` },
				previousTier,
			);
		}

		// Large tool outputs suggest complex operations
		if (toolResults.some((r) => toolResultTextLength(r) > LARGE_TOOL_OUTPUT_THRESHOLD)) {
			return applyHysteresis({ tier: "powerful", reason: "large tool output detected" }, previousTier);
		}

		// All read-only tools and no errors -> check for simple
		if (allToolCallsReadOnly(toolCalls) && !hasErrors) {
			// If the last user message is also short and simple, use fast tier.
			// Only de-escalate in short conversations; in longer ones a read-only
			// turn in the middle of complex work shouldn't drop to fast.
			if (lastUserMessage && messages.length < READ_ONLY_DEESCALATION_MAX_MESSAGES) {
				const text = getUserMessageText(lastUserMessage);
				if (text.length < SHORT_MESSAGE_THRESHOLD && !containsComplexKeyword(text)) {
					return applyHysteresis(
						{ tier: "fast", reason: "read-only tools with short user message" },
						previousTier,
					);
				}
			}
		}
	}

	// Check user message for complex keywords
	if (lastUserMessage) {
		const text = getUserMessageText(lastUserMessage);
		if (containsComplexKeyword(text)) {
			return applyHysteresis({ tier: "powerful", reason: "complex keywords in user message" }, previousTier);
		}

		// Short question with no prior assistant context -> fast
		if (lastAssistantIndex < 0 && text.length < SHORT_MESSAGE_THRESHOLD) {
			return applyHysteresis({ tier: "fast", reason: "short initial message" }, previousTier);
		}
	}

	return applyHysteresis({ tier: "balanced", reason: "default" }, previousTier);
}

/**
 * Budget-aware tier capping: as an active goal budget depletes, cap the
 * tier so the loop spends the expensive turns early and rations later
 * instead of hitting the wall at full price.
 */
export function applyBudgetCap(
	result: { tier: ComplexityTier; reason: string },
	budgetUsedFraction: number | undefined,
): { tier: ComplexityTier; reason: string } {
	if (budgetUsedFraction === undefined) return result;
	const pct = Math.round(budgetUsedFraction * 100);
	if (budgetUsedFraction >= BUDGET_CAP_FAST_FRACTION && result.tier !== "fast") {
		return { tier: "fast", reason: `${result.reason} (capped to fast: ${pct}% of goal budget spent)` };
	}
	if (budgetUsedFraction >= BUDGET_CAP_BALANCED_FRACTION && result.tier === "powerful") {
		return { tier: "balanced", reason: `${result.reason} (capped to balanced: ${pct}% of goal budget spent)` };
	}
	return result;
}

/**
 * Hysteresis: prevent oscillation by not dropping from powerful directly to fast.
 * If the previous turn was powerful and the current classification is fast,
 * dampen to balanced instead.
 */
function applyHysteresis(
	result: { tier: ComplexityTier; reason: string },
	previousTier: ComplexityTier | undefined,
): { tier: ComplexityTier; reason: string } {
	if (previousTier === "powerful" && result.tier === "fast") {
		return { tier: "balanced", reason: `${result.reason} (dampened from fast: previous turn was powerful)` };
	}
	return result;
}

// ============================================================================
// Model selection
// ============================================================================

/** Combined per-token cost used to rank models within a tier. */
function combinedCost(model: Model<Api>): number {
	return model.cost.input + model.cost.output;
}

/**
 * Rank candidates within a tier. Lower rank wins. Ordering:
 * 1. Same provider as the preferred model (consistent auth, billing, behavior)
 * 2. For balanced/powerful: reasoning-capable models first
 * 3. Paid models before free ones (free tiers are rate-limited/unreliable)
 * 4. Cheaper combined cost first
 * 5. Larger context window as the final tiebreak
 */
function compareCandidates(a: Model<Api>, b: Model<Api>, tier: ComplexityTier, preferred: Model<Api>): number {
	const aSameProvider = a.provider === preferred.provider ? 0 : 1;
	const bSameProvider = b.provider === preferred.provider ? 0 : 1;
	if (aSameProvider !== bSameProvider) return aSameProvider - bSameProvider;

	if (tier !== "fast") {
		const aReasoning = a.reasoning ? 0 : 1;
		const bReasoning = b.reasoning ? 0 : 1;
		if (aReasoning !== bReasoning) return aReasoning - bReasoning;
	}

	const aFree = combinedCost(a) === 0 ? 1 : 0;
	const bFree = combinedCost(b) === 0 ? 1 : 0;
	if (aFree !== bFree) return aFree - bFree;

	const costDiff = combinedCost(a) - combinedCost(b);
	if (costDiff !== 0) return costDiff;

	return b.contextWindow - a.contextWindow;
}

/**
 * Select the best model for a given complexity tier from available models.
 * A user-pinned model for the tier wins over the heuristic ranking.
 * When the conversation contains images, only vision-capable models are
 * considered so routing can never break a multimodal session.
 */
export function selectModelForTier(
	tier: ComplexityTier,
	config: SmartRouterConfig,
	requiresImageInput = false,
): Model<Api> {
	const { availableModels, preferredModel } = config;

	// User pin wins, as long as it is modality-compatible.
	const pinned = config.pinnedModels?.[tier];
	if (pinned && (!requiresImageInput || pinned.input.includes("image"))) {
		return pinned;
	}

	// Batch variants are slower by design and must never hijack interactive
	// routing just because they are cheap. They are only reachable through
	// the explicit goal-economy swap (findBatchSibling) or a user pin.
	const nonBatch = availableModels.filter((m) => !m.id.endsWith(":batch"));
	const candidates = requiresImageInput ? nonBatch.filter((m) => m.input.includes("image")) : nonBatch;

	// Partition models into tiers
	const tierModels = candidates.filter((m) => modelTier(m) === tier);

	if (tierModels.length === 0) {
		// Requested tier has no (compatible) models, fall back to preferred
		return preferredModel;
	}

	let best = tierModels[0];
	for (let i = 1; i < tierModels.length; i++) {
		if (compareCandidates(tierModels[i], best, tier, preferredModel) < 0) {
			best = tierModels[i];
		}
	}
	return best;
}

/**
 * Find a model's half-price `:batch` sibling on the same provider, if the
 * registry has one. Batch variants are ordinary streaming models with
 * relaxed latency and roughly half the price — a fit for unattended work.
 */
export function findBatchSibling(model: Model<Api>, availableModels: readonly Model<Api>[]): Model<Api> | undefined {
	if (model.id.endsWith(":batch")) return undefined;
	const siblingId = `${model.id}:batch`;
	return availableModels.find((m) => m.provider === model.provider && m.id === siblingId);
}

// ============================================================================
// Main entry
// ============================================================================

/**
 * Analyze the turn and return a model recommendation.
 *
 * Returns undefined only when smart routing is disabled. When the selected
 * model equals the preferred model, the result is still returned with
 * `changed: false` so callers can track the classified tier for hysteresis.
 */
const THINKING_ORDER: readonly ThinkingLevel[] = ["off", "minimal", "low", "medium", "high", "xhigh"];

/**
 * Thinking budget that matches a tier: reasoning effort is a cost dial just
 * like model choice. A directory listing does not need "high"; a refactor
 * does. The session level is the ceiling - routing never raises it.
 */
const TIER_MAX_THINKING: Record<ComplexityTier, ThinkingLevel> = {
	fast: "minimal",
	balanced: "medium",
	powerful: "xhigh",
};

/**
 * Pick the thinking level for a turn: the lower of the session level and the
 * tier's ceiling. Returns undefined when the session level already fits (no
 * change needed) or the model cannot reason at all.
 */
export function thinkingLevelForTier(
	tier: ComplexityTier,
	sessionLevel: ThinkingLevel,
	model: Model<Api>,
): ThinkingLevel | undefined {
	if (!model.reasoning || sessionLevel === "off") return undefined;
	const ceiling = TIER_MAX_THINKING[tier];
	if (THINKING_ORDER.indexOf(sessionLevel) <= THINKING_ORDER.indexOf(ceiling)) return undefined;
	return ceiling;
}

export function routeNextTurn(
	analysis: TurnAnalysis,
	config: SmartRouterConfig,
	previousTier?: ComplexityTier,
): SmartRouterResult | undefined {
	if (!config.enabled) return undefined;

	const { tier, reason } = applyBudgetCap(classifyComplexity(analysis, previousTier), analysis.budgetUsedFraction);
	const requiresImageInput = conversationHasImages(analysis.messages);
	let model = selectModelForTier(tier, config, requiresImageInput);
	let finalReason = reason;

	// Economy mode: during a budgeted goal loop, prefer the half-price batch
	// sibling of the routed model. Pins are the user's explicit model choice
	// and are never swapped.
	const pinned = config.pinnedModels?.[tier];
	const isPinnedPick = pinned !== undefined && modelsAreEqual(model, pinned);
	if (config.preferBatchDuringGoal && analysis.budgetUsedFraction !== undefined && !isPinnedPick) {
		const sibling = findBatchSibling(model, config.availableModels);
		if (sibling) {
			model = sibling;
			finalReason = `${finalReason} (batch variant: ~half price for unattended goal work)`;
		}
	}

	const thinkingLevel = thinkingLevelForTier(tier, analysis.thinkingLevel, model);
	if (thinkingLevel) {
		finalReason = `${finalReason}; thinking ${analysis.thinkingLevel} → ${thinkingLevel} for this turn`;
	}

	return {
		model,
		tier,
		reason: finalReason,
		changed: !modelsAreEqual(model, config.preferredModel),
		thinkingLevel,
	};
}
